// src/components/StatusPage.js
import React from 'react';
import { Box, Container, CssBaseline, Paper, Stack, Typography } from '@mui/material';
import { CircleCheckBig, CircleX, TriangleAlert } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { BrandSpinner } from './Loading';

/**
 * The outcome pages — payment confirmed, cancelled, still processing — drawn
 * from one shape so an order does not appear to end on a different website
 * depending on how it went.
 *
 * `tone` chooses the badge, and nothing else: the copy is always the caller's,
 * because only the caller knows what actually happened.
 */
const TONES = {
  success: { Icon: CircleCheckBig, color: 'success.main', tint: 'rgba(56,142,60,0.12)' },
  warning: { Icon: TriangleAlert, color: 'warning.main', tint: 'rgba(228,179,67,0.16)' },
  error: { Icon: CircleX, color: 'error.main', tint: 'rgba(178,34,34,0.12)' },
};

const StatusBadge = ({ tone }) => {
  if (tone === 'pending') {
    return <BrandSpinner size={64} />;
  }

  const { Icon, color, tint } = TONES[tone] || TONES.success;
  return (
    <Box
      sx={{
        width: 76,
        height: 76,
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        color,
        backgroundColor: tint,
      }}
    >
      <Icon size={34} strokeWidth={1.8} />
    </Box>
  );
};

const StatusPage = ({ tone = 'success', title, children, actions }) => (
  <>
    <CssBaseline />
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />

      <Container
        maxWidth="sm"
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 5, md: 8 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: { xs: 3.5, md: 5 },
            textAlign: 'center',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack alignItems="center" sx={{ mb: 3 }}>
            <StatusBadge tone={tone} />
          </Stack>

          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            {title}
          </Typography>

          <Box sx={{ color: 'text.secondary' }}>{children}</Box>

          {actions && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="center"
              sx={{ mt: 4 }}
            >
              {actions}
            </Stack>
          )}
        </Paper>
      </Container>

      <Footer />
    </Box>
  </>
);

export default StatusPage;
